/**
 * Dependencies
 */
import http from '../../../services/HttpService'
import {
    getUsers,
    updateUserAdminStatus
} from '../../../services/CompanyService'

/**
 * Set the company data state object
 *
 * @param  {Function} options.commit
 * @param  {Object} company
 * @return {Void}
 */
const setCompanyData = ({ commit }, company) => {
    commit('SET_COMPANY_DATA', company)
}

/**
 * Set the company's user list
 *
 * @param  {Function} options.commit
 * @param  {Array} users
 * @return {Void}
 */
const setCompanyUsers = ({ commit }, users) => {
    commit('SET_COMPANY_USERS', users)
}

/**
 * Retrieve a list of company users
 *
 * @param  {Function} options.commit
 * @return {Promise}
 */
const getCompanyUsers = ({ commit, state }) => {
    return getUsers(state.data.id)
        .then(res => commit('SET_COMPANY_USERS_SUCCESS', res.data.data))
        .catch(err => commit('SET_COMPANY_USERS_FAILURE', err))
}

/**
 * Update a user's privilege status for a company
 *
 * @param  {Function} options.commit
 * @param  {Object} user
 * @return {Void}
 */
const updateUserDetails = ({ commit }, payload) => {
    updateUserAdminStatus(payload.company, payload.user, payload.privilege)
}

/**
 * Remove a single user from a given company
 *
 * @param  {Function} options.commit
 * @param  {Object} payload
 * @return {Promise}
 */
const removeCompanyUser = ({ commit, state, dispatch }, id) => {
    return http.del(`auth/companies/${state.data.id}/users/${id}/`)
        .then(res => commit('REMOVE_COMPANY_USER_SUCCESS', res))
        .then(() => dispatch('getCompanyUsers'))
        .then(() => dispatch('addNotification', {
            title: 'User Removed from Company',
            type: 'success',
            text: 'The user was successfully removed',
            timeout: true
        }, { root: true }))
        .catch(err => commit('REMVE_COMPANY_USER_FAILURE', err))
}

/**
 * Update company data
 *
 * @param  {Function} options.commit
 * @return {Promise}
 */
const updateCompanyInformation = ({ commit, dispatch, state }, payload) => new Promise((resolve, reject) => {
    http.patch(`auth/companies/${state.data.id}/`, payload)
        .then(res => {
            commit('UPDATE_COMPANY_INFO_SUCCESS', res.data.data)
            dispatch('addNotification', {
                title: 'Company Successfully Updated',
                type: 'success',
                text: res.data.message,
                timeout: true
            }, { root: true })
            resolve(res)
        })
        .catch(err => {
            commit('UPDATE_COMPANY_INFO_FAILURE', err)
            dispatch('addNotification', {
                title: 'Company failed to update',
                type: 'error',
                text: 'Your company information was not saved.',
                timeout: true
            }, { root: true })
            reject(err)
        })
})

/**
 * Get the outbound invitation data
 *
 * @param  {Function} options.commit
 * @return {Void}
 */
const getOutboundInvitations = ({ commit, state }) => {
    http.get(`auth/companies/${state.data.id}/company-user-invitations/`)
        .then(res => commit('OUTBOUND_INVITES_SUCCESS', res.data))
        .catch(err => console.log(err))
}

/**
 * Get the inbound invitation data
 *
 * @param  {Function} options.commit
 * @return {Void}
 */
const getInboundInvitations = ({ commit }) => {
    http.get('auth/company-invitations/')
        .then(res => commit('INBOUND_INVITES_SUCCESS', res.data))
        .catch(err => console.log(err))
}

/**
 * Handle inbound invite acceptance
 *
 * @param  {Function} options.commit
 * @return {Void}
 */
const acceptInboundCompanyInvitation = ({ commit, dispatch }, id) => {
    http.put(`auth/company-invitations/${id}/`)
        .then(() => dispatch('addNotification', {
            title: 'Success',
            type: 'success',
            text: 'You have successfully accepted the company\'s invitation.',
            timeout: true
        }, { root: true }))
        .then(() => dispatch('getInboundInvitations'))
        .catch(err => console.log(err))
}

/**
 * Handle inbound invite rejection
 *
 * @param  {Function} options.commit
 * @return {Void}
 */
const rejectInboundCompanyInvitation = ({ commit, dispatch }, id) => {
    http.del(`auth/company-invitations/${id}/`)
        .then(() => dispatch('getInboundInvitations'))
        .catch(err => console.log(err))
}

/**
 * Invite users to company by an array of emails
 *
 * @param  {Function} options.commit
 * @param  {Object} options.state
 * @param  {Array} emails
 * @return {Promise}
 */
const inviteUsersToCompany = ({ commit, state, dispatch }, emails) => {
    return new Promise((resolve, reject) => {
        http.patch(`auth/companies/${state.data.id}/users/?action=add`, { emails: emails })
            .then(res => {
                dispatch('getOutboundInvitations')
                resolve(res.data.message[0])
            })
            .catch(err => {
                reject(err)
            })
    })
}

/**
 * Remove an outbound company invitation
 *
 * @param  {Function} options.commit
 * @param  {Number} id
 * @return {Void}
 */
const removeOutboundInvitation = ({ commit, state, dispatch }, id) => {
    http.del(`auth/companies/${state.data.id}/invitations/${id}/`)
        .then(() => dispatch('getOutboundInvitations'))
        .then(() => dispatch('addNotification', {
            title: 'Success',
            type: 'success',
            text: 'Invitation successfully revoked.',
            timeout: true
        }, { root: true }))
        .catch(err => dispatch('addNotification', {
            title: 'Error',
            type: 'error',
            text: err.response.data.message,
            timeout: true
        }, { root: true }))
}

/**
 * Update the email of an existing outbound invitation
 *
 * @param  {Function} options.commit
 * @return {Void}
 */
const updateOutboundInvitation = ({ commit, state }, payload) => {
    return http.patch(`auth/companies/${state.data.id}/invitations/${payload.id}/`, {
        email: payload.email
    })
}

/**
 * Update the invite state with the new email address
 *
 * @param  {Function} options.commit
 * @param  {Object} payload
 * @return {Promise}
 */
const modifyOutboundInviteState = ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
        commit('UPDATE_INVITE_EMAIL', payload)
        resolve()
    })
}

/**
 * Set the branding for a company
 *
 * @param  {Function} options.commit
 * @param  {FormData} data
 * @return {Promise}
 */
const setBranding = ({ commit, state }, data) => new Promise((resolve, reject) => {
    return http.post(`auth/companies/${state.data.id}/branding/`, data)
})

export default {
    setBranding,
    getCompanyUsers,
    setCompanyUsers,
    updateUserDetails,
    removeCompanyUser,
    setCompanyData,
    inviteUsersToCompany,
    getInboundInvitations,
    getOutboundInvitations,
    updateCompanyInformation,
    removeOutboundInvitation,
    updateOutboundInvitation,
    modifyOutboundInviteState,
    acceptInboundCompanyInvitation,
    rejectInboundCompanyInvitation
}



// WEBPACK FOOTER //
// ./src/state/modules/company/actions.js